import { motion } from 'framer-motion'
import { SCENES, MIC_SCENES } from './sceneContent'
import { accentFor, EASE } from './scenes2d/theme'
import { toggleMic, useMicState } from './micAudio'
import { Scene01Birth2D } from './scenes2d/Scene01Birth2D'
import { Scene02Waveform2D } from './scenes2d/Scene02Waveform2D'
import { Scene03Fourier2D } from './scenes2d/Scene03Fourier2D'
import { Scene04Spectrogram2D } from './scenes2d/Scene04Spectrogram2D'
import { Scene05Noise2D } from './scenes2d/Scene05Noise2D'
import { Scene06Speech2D } from './scenes2d/Scene06Speech2D'
import { Scene07Embeddings2D } from './scenes2d/Scene07Embeddings2D'
import { Scene08Clone2D } from './scenes2d/Scene08Clone2D'
import { Scene09TTS2D } from './scenes2d/Scene09TTS2D'
import { Scene10Emotion2D } from './scenes2d/Scene10Emotion2D'
import { Scene11Agents2D } from './scenes2d/Scene11Agents2D'
import { Scene12Universe2D } from './scenes2d/Scene12Universe2D'
import { Scene13Roadmap2D } from './scenes2d/Scene13Roadmap2D'

// Scenes not yet ported to SVG fall back to a plain accent pulse so every
// chapter still renders something while the rest are built out.
function ScenePlaceholder2D({ accent }) {
  return (
    <svg viewBox="0 0 320 160" style={{ width: '100%', maxWidth: 340, height: 'auto', display: 'block', margin: '0 auto' }}>
      <style>{`
        @keyframes placeholderPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.92); }
          50% { opacity: 0.9; transform: scale(1); }
        }
      `}</style>
      <circle
        cx="160" cy="80" r="14" fill={accent}
        style={{ transformOrigin: '160px 80px', animation: 'placeholderPulse 1.8s ease-in-out infinite' }}
      />
    </svg>
  )
}

const SCENES_2D = [
  Scene01Birth2D,
  Scene02Waveform2D,
  Scene03Fourier2D,
  Scene04Spectrogram2D,
  Scene05Noise2D,
  Scene06Speech2D,
  Scene07Embeddings2D,
  Scene08Clone2D,
  Scene09TTS2D,
  Scene10Emotion2D,
  Scene11Agents2D,
  Scene12Universe2D,
  Scene13Roadmap2D,
]

// Mobile alternative to the desktop's fixed-viewport R3F/WebGL cross-fade:
// every chapter sits in normal document flow as an SVG-only scene and the
// page scrolls natively, instead of drei's ScrollControls-driven Canvas.
export function AudioMobile() {
  const mic = useMicState()

  return (
    <div style={{ position: 'relative', background: '#020212' }}>
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        padding: '16px clamp(16px, 5vw, 32px)',
        fontFamily: "'Clash Display', sans-serif",
        fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
        color: 'rgba(255,255,255,0.9)',
        background: 'linear-gradient(180deg, rgba(2,2,18,0.85), transparent)',
        pointerEvents: 'none',
      }}>
        Audio AI
      </div>

      <button
        onClick={toggleMic}
        style={{
          position: 'fixed', bottom: 14, left: 14, zIndex: 100,
          cursor: 'pointer', fontFamily: 'monospace', fontSize: 9,
          letterSpacing: '0.08em',
          color: mic.active ? '#00ddff' : '#7fa8c0',
          padding: '6px 10px', borderRadius: 999,
          border: `1px solid ${mic.active ? 'rgba(0,221,255,0.6)' : 'rgba(80,170,230,0.3)'}`,
          background: mic.active ? 'rgba(0,180,230,0.18)' : 'rgba(20,40,60,0.45)',
          backdropFilter: 'blur(4px)',
        }}
      >
        {mic.pending ? '◌ ENABLING…' : mic.active ? '● LIVE MIC ON' : '○ USE MY MIC'}
        {mic.error && <span style={{ color: '#ff6688', marginLeft: 8 }}>{mic.error}</span>}
      </button>

      {SCENES.map((s, i) => {
        const accent = accentFor(i)
        const Scene = SCENES_2D[i] || ScenePlaceholder2D
        return (
          <motion.section
            key={i}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.6, ease: EASE }}
            style={{
              position: 'relative',
              minHeight: '100svh',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: '64px 0 32px',
            }}
          >
            <div style={{
              padding: '0 clamp(16px, 5vw, 24px)', marginBottom: 8,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
              color: accent, display: 'flex', alignItems: 'center', gap: 7,
            }}>
              <div style={{ width: 18, height: 1, background: accent, opacity: 0.7 }} />
              Chapter {String(i + 1).padStart(2, '0')} · {s.title}
            </div>

            <div style={{ padding: '12px clamp(16px, 5vw, 24px)' }}>
              <Scene accent={accent} />
            </div>

            <div style={{ padding: '4px clamp(16px, 5vw, 24px) 0' }}>
              <div style={{
                fontSize: 15, color: '#7fa8c0', fontFamily: 'system-ui, sans-serif',
                marginBottom: 8,
              }}>
                {s.subtitle}
              </div>
              <div style={{
                fontSize: 13, lineHeight: 1.55, color: '#9fb6c4',
                fontFamily: 'system-ui, sans-serif', marginBottom: 12,
              }}>
                {s.desc}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span style={{
                  fontSize: 8, letterSpacing: '0.18em', color: '#4a6680',
                  textTransform: 'uppercase', fontFamily: 'monospace', marginRight: 2,
                }}>
                  Used in
                </span>
                {s.apps.map((app) => (
                  <span key={app} style={{
                    fontSize: 9, color: '#aee3ff', fontFamily: 'monospace',
                    padding: '3px 8px', borderRadius: 999,
                    border: '1px solid rgba(80,170,230,0.3)',
                    background: 'rgba(40,120,180,0.12)', whiteSpace: 'nowrap',
                  }}>
                    {app}
                  </span>
                ))}
              </div>
              {MIC_SCENES.includes(i) && (
                <div style={{
                  marginTop: 10, fontFamily: 'monospace', fontSize: 9,
                  letterSpacing: '0.1em', color: '#5fb8e0',
                }}>
                  ↑ speak to see your own voice
                </div>
              )}
            </div>
          </motion.section>
        )
      })}
    </div>
  )
}
